import { z } from 'zod'
import { utcTimestampSchema } from './common'

/**
 * Dataset contract of the model-training capability, as served by the backend
 * (backend change `add-dataset-list-api`, OpenAPI at `/openapi.json`):
 *   GET /datasets?q&sort&order&page&pageSize   → DatasetList
 *   GET /datasets/summary?q                    → DatasetSummary
 * One dataset, for the Data Preview step (backend change `add-dataset-preview-api`):
 *   GET /datasets/{id}                         → Dataset, whatever its status
 *                                                | 404 dataset_not_found | 422 malformed id
 *   GET /datasets/{id}/features                → DatasetFeatureStatistic[], by name; the
 *                                                distinct values of a column come with backend
 *                                                change `add-feature-value-counts`
 *   GET /datasets/{id}/interval-distribution   → IntervalBucket[], shortest interval first
 *   GET /datasets/{id}/preview?limit=30|50|100 → DatasetPreview, last record of the file first
 * The last three answer 409 dataset_not_completed for a dataset in progress or in error; the
 * preview also 409 dataset_file_missing | 409 dataset_file_unsupported | 503 storage_unavailable.
 * Edit and delete (backend guide `frontend-dataset-edit-delete-integration.md`):
 *   PATCH /datasets/{id}                       → 200 Dataset; body: { name, description }, both
 *                                                required, also when only one changed
 *                                                | 404 dataset_not_found | 409 dataset_name_taken
 *                                                | 422 validation body (`detail[].loc` names the field)
 *   POST /datasets/delete                      → 204, every listed dataset is deleted, or none;
 *                                                body: { ids } (1 to 100)
 *                                                | 404 dataset_not_found
 *                                                | 409 dataset_upload_in_progress
 *   One dataset is deleted with the same call and one id: there is no DELETE /datasets/{id}
 *   (405).
 * All need the access token. Field names are camelCase.
 */
export const DATASET_STATUSES = ['in_progress', 'completed', 'error'] as const
export const datasetStatusSchema = z.enum(DATASET_STATUSES)
export type DatasetStatus = z.infer<typeof datasetStatusSchema>

const countSchema = z.number().int().nonnegative()

export const datasetSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable(),
  /** The three counts are `null` until the analysis of the uploaded file completes. */
  recordCount: countSchema.nullable(),
  featureCount: countSchema.nullable(),
  sizeBytes: countSchema,
  /** Full name of the creator; `null` when the dataset has none. */
  createdBy: z.string().nullable(),
  createdAt: utcTimestampSchema,
  status: datasetStatusSchema,
  missingRecordCount: countSchema.nullable(),
  /** Asset the data was collected from, when the dataset is linked to one. */
  assetName: z.string().nullable(),
})
export type Dataset = z.infer<typeof datasetSchema>

export const DATASET_SORT_FIELDS = [
  'name',
  'recordCount',
  'featureCount',
  'sizeBytes',
  'createdBy',
  'createdAt',
  'status',
  'missingRecordCount',
] as const
export const datasetSortFieldSchema = z.enum(DATASET_SORT_FIELDS)
export type DatasetSortField = z.infer<typeof datasetSortFieldSchema>

export { sortOrderSchema, type SortOrder } from './common'

export const datasetListSchema = z.object({
  items: z.array(datasetSchema),
  /** Datasets matching the search, not only those on this page. */
  total: countSchema,
})
export type DatasetList = z.infer<typeof datasetListSchema>

export const datasetSummarySchema = z.object({
  total: countSchema,
  completed: countSchema,
  withoutMissingRecords: countSchema,
  withIssues: countSchema,
})
export type DatasetSummary = z.infer<typeof datasetSummarySchema>

/** Rows of one distinct value of a column: an integer, a string or a boolean, as in the file. */
export const featureValueCountSchema = z.object({
  value: z.union([z.number(), z.string(), z.boolean()]),
  count: countSchema,
})
export type FeatureValueCount = z.infer<typeof featureValueCountSchema>

/** Above it the worker does not count the values of a column (`distinctCount` is `null`). */
export const FEATURE_VALUE_COUNT_LIMIT = 50

/** Statistics of one feature; the four statistics are `null` for a feature without them. */
export const datasetFeatureStatisticSchema = z.object({
  name: z.string().min(1),
  dataType: z.string(),
  count: countSchema.nullable(),
  missingCount: countSchema.nullable(),
  mean: z.number().nullable(),
  std: z.number().nullable(),
  min: z.number().nullable(),
  max: z.number().nullable(),
  /**
   * Distinct non-missing values of an `integer`, `string` or `boolean` column, at most 50.
   * `null`: a decimal column, more than 50 values, or not counted (dataset processed before the
   * values were counted, or an older backend that leaves the field out).
   */
  distinctCount: countSchema.nullable().default(null),
  /** Rows per distinct value, ordered by value; `null` when `distinctCount` is. */
  valueCounts: z.array(featureValueCountSchema).nullable().default(null),
})
export type DatasetFeatureStatistic = z.infer<typeof datasetFeatureStatisticSchema>
export const datasetFeatureStatisticsSchema = z.array(datasetFeatureStatisticSchema)

/** How many records follow their predecessor after `intervalSeconds`. */
export const intervalBucketSchema = z.object({
  intervalSeconds: countSchema,
  count: countSchema,
})
export type IntervalBucket = z.infer<typeof intervalBucketSchema>
export const intervalDistributionSchema = z.array(intervalBucketSchema)

export const DATASET_PREVIEW_LIMITS = [30, 50, 100] as const
export type DatasetPreviewLimit = (typeof DATASET_PREVIEW_LIMITS)[number]
export const DEFAULT_DATASET_PREVIEW_LIMIT: DatasetPreviewLimit = 30

/** A cell of the file: a number, text as written, or `null` for a missing value. */
export const datasetPreviewValueSchema = z.union([z.number(), z.string(), z.null()])
export type DatasetPreviewValue = z.infer<typeof datasetPreviewValueSchema>

export const datasetPreviewSchema = z
  .object({
    /** The file's columns without the timestamp column. */
    columns: z.array(z.string()),
    rows: z.array(
      z.object({
        /** As written in the file. */
        timestamp: z.string(),
        /** One per column, in the order of `columns`. */
        values: z.array(datasetPreviewValueSchema),
      }),
    ),
  })
  // Shifted cells would be shown under the wrong column: such an answer is refused.
  .refine((preview) => preview.rows.every((row) => row.values.length === preview.columns.length), {
    message: 'every row must have one value per column',
  })
export type DatasetPreview = z.infer<typeof datasetPreviewSchema>

/**
 * Dataset upload contract of the backend API v1 (OpenAPI: UploadCreateIn, UploadCreateOut,
 * UploadPartOut, UploadIncompleteOut, ErrorOut). The file goes from the browser directly to the
 * object storage; the API is called to open the upload and to complete it, or to cancel it.
 *   POST /datasets/uploads/presign               → 201 upload + one address per part
 *                                                | 413 file_too_large | 429 too_many_uploads
 *                                                | 503 storage_unavailable
 *                                                  body: { file_name, size_bytes }
 *   PUT  <parts[n].url>                          raw bytes of part n, to the storage, no API header
 *                                                → 200 | 403 address expired or altered, or the
 *                                                  body does not have the size of the part
 *   POST /datasets/uploads/{upload_id}/complete  → 200 { completed: true }, also when repeated
 *                                                | 409 upload_incomplete | 404 upload_not_found
 *                                                | 503 storage_unavailable
 *   DELETE /datasets/uploads/{upload_id}         → 204 the upload and its stored bytes are removed
 *                                                | 404 upload_not_found (also when repeated)
 *                                                | 409 upload_in_use | 503 storage_unavailable
 * There is no call to read the state of an upload or to get new addresses.
 * Field names are the backend's (snake_case).
 */
export const uploadCreateRequestSchema = z.object({
  /** Ends in `.csv`, any letter case. */
  file_name: z.string().min(1),
  size_bytes: z.number().int().positive(),
})
export type UploadCreateRequest = z.infer<typeof uploadCreateRequestSchema>

export const uploadPartSchema = z.object({
  /** Part `index` covers the bytes `index * chunk_size` up to `(index + 1) * chunk_size`. */
  index: z.number().int().nonnegative(),
  /** Presigned address of the storage; the signature in it is the permission. */
  url: z.string().min(1),
})
export type UploadPart = z.infer<typeof uploadPartSchema>

export const uploadCreateResponseSchema = z.object({
  /** UUID; opaque to the frontend. */
  upload_id: z.string().min(1),
  /** Decided by the server: every part has this size except the last. */
  chunk_size: z.number().int().positive(),
  total_chunks: z.number().int().positive(),
  /** Decided by the server: at most this many parts are sent to the storage at the same time. */
  max_parallel_parts: z.number().int().positive(),
  /** Every address stops working at this time. */
  expires_at: utcTimestampSchema,
  parts: z.array(uploadPartSchema).min(1),
})
export type UploadCreateResponse = z.infer<typeof uploadCreateResponseSchema>

export const uploadCompleteResponseSchema = z.object({
  completed: z.literal(true),
})
export type UploadCompleteResponse = z.infer<typeof uploadCompleteResponseSchema>

export const UPLOAD_ERROR_CODES = {
  fileTooLarge: 'file_too_large',
  storageUnavailable: 'storage_unavailable',
  /** The user already has as many uploads no dataset uses as the server allows. */
  tooManyUploads: 'too_many_uploads',
  uploadIncomplete: 'upload_incomplete',
  /** A dataset was created from the upload: it cannot be cancelled. */
  uploadInUse: 'upload_in_use',
  uploadNotFound: 'upload_not_found',
} as const

/** Body of the 409 answer to a completion: these parts are missing or have the wrong size. */
export const uploadIncompleteErrorSchema = z.object({
  code: z.literal(UPLOAD_ERROR_CODES.uploadIncomplete),
  message: z.string(),
  missing_chunk_indexes: z.array(z.number().int().nonnegative()).min(1),
})
export type UploadIncompleteError = z.infer<typeof uploadIncompleteErrorSchema>

/**
 * Dataset creation contract of the backend API v1:
 *   POST /datasets  → 201 the new dataset, `in_progress` until the file has been processed
 *                   | 404 upload_not_found | 409 dataset_name_taken | 409 upload_already_used
 *                   | 422 validation body (`detail[].loc` names the field)
 * Request and answer are snake_case, unlike the dataset list. Column names are sent exactly as
 * written in the file's header: not trimmed, letter case kept.
 */
export const DATASET_DATA_TYPES = ['double', 'integer', 'string', 'boolean'] as const
export const datasetDataTypeSchema = z.enum(DATASET_DATA_TYPES)
export type DatasetDataType = z.infer<typeof datasetDataTypeSchema>

export const DATASET_NAME_MAX_LENGTH = 255
export const DATASET_COLUMN_NAME_MAX_LENGTH = 255
export const DATASET_MAX_FEATURES = 1000

const columnNameSchema = z.string().min(1).max(DATASET_COLUMN_NAME_MAX_LENGTH)

export const datasetFeatureSchema = z.object({
  name: columnNameSchema,
  /** Legacy preview hint; the worker resolves the final type. */
  data_type: datasetDataTypeSchema.optional(),
})
export type DatasetFeature = z.infer<typeof datasetFeatureSchema>

export const datasetCreateRequestSchema = z.object({
  /** Unique among the datasets that are not deleted, ignoring letter case. */
  name: z.string().min(1).max(DATASET_NAME_MAX_LENGTH),
  description: z.string().optional(),
  /** An upload opened by the same user that no dataset uses yet. */
  upload_id: z.string().min(1),
  /** Not one of the features. */
  timestamp_column: columnNameSchema,
  timestamp_format: z.string().min(1).max(100),
  features: z.array(datasetFeatureSchema).min(1).max(DATASET_MAX_FEATURES),
})
export type DatasetCreateRequest = z.infer<typeof datasetCreateRequestSchema>

export const datasetCreateResponseSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  description: z.string().nullable(),
  file_name: z.string().min(1),
  file_size_bytes: countSchema,
  /** `null` until the file has been processed. */
  record_count: countSchema.nullable(),
  /** The selected features; the timestamp column is not counted. */
  feature_count: countSchema,
  missing_record_count: countSchema.nullable(),
  status: datasetStatusSchema,
  created_at: utcTimestampSchema,
  created_by: z.string().nullable(),
})
export type DatasetCreateResponse = z.infer<typeof datasetCreateResponseSchema>

/** Body of an edit: both fields on every call; the server ignores the spaces around them. */
export const datasetUpdateRequestSchema = z.object({
  /** Unique among the datasets that are not deleted, ignoring letter case; its own name is allowed. */
  name: z.string().trim().min(1).max(DATASET_NAME_MAX_LENGTH),
  description: z.string().trim().min(1),
})
export type DatasetUpdateRequest = z.infer<typeof datasetUpdateRequestSchema>

/** The largest page size of the list. */
export const DATASET_DELETE_MAX_IDS = 100

export const datasetDeleteRequestSchema = z.object({
  ids: z.array(z.string().min(1)).min(1).max(DATASET_DELETE_MAX_IDS),
})
export type DatasetDeleteRequest = z.infer<typeof datasetDeleteRequestSchema>

export const DATASET_ERROR_CODES = {
  nameTaken: 'dataset_name_taken',
  /** A dataset was already created from the upload. */
  uploadAlreadyUsed: 'upload_already_used',
  /** Unknown or deleted dataset. */
  notFound: 'dataset_not_found',
  /** In progress or in error: it has no statistics, distribution or preview. */
  notCompleted: 'dataset_not_completed',
  fileMissing: 'dataset_file_missing',
  /** The last records of the file cannot be read reliably. */
  fileUnsupported: 'dataset_file_unsupported',
  storageUnavailable: 'storage_unavailable',
  /** Unknown archive job, a removed one, or a job of another user. */
  downloadNotFound: 'download_not_found',
  /** More datasets than one archive may hold. */
  tooManyDatasets: 'too_many_datasets',
  /** The files are too large together for one archive. */
  downloadTooLarge: 'download_too_large',
  /** The user already has the allowed number of archives waiting or being built. */
  tooManyDownloads: 'too_many_downloads',
  /** The file of the dataset is still being sent: it cannot be deleted yet. */
  uploadInProgress: 'dataset_upload_in_progress',
} as const

/**
 * Download contract (backend change `add-dataset-download`). No file bytes come from the API:
 * an answer carries an address of the object storage that the browser fetches itself.
 *   GET  /datasets/{id}/download   → DatasetDownload
 *                                  | 404 dataset_not_found | 409 dataset_file_missing
 *                                  | 503 storage_unavailable
 *   POST /dataset-downloads        → 202 DatasetArchiveJob (`pending`)
 *                                  | 404 dataset_not_found, 409 dataset_file_missing (both with
 *                                    `datasetIds`) | 413 download_too_large
 *                                  | 422 too_many_datasets or the validation body
 *                                  | 429 too_many_downloads | 503 storage_unavailable
 *   GET  /dataset-downloads/{id}   → DatasetArchiveJob | 404 download_not_found
 * Field names are camelCase.
 */
export const datasetDownloadSchema = z.object({
  /** Works without the access token, for a few minutes. Never stored or logged. */
  downloadUrl: z.string().min(1),
  /** The name the storage offers the file under. */
  fileName: z.string().min(1),
  expiresAt: utcTimestampSchema,
})
export type DatasetDownload = z.infer<typeof datasetDownloadSchema>

export const datasetArchiveRequestSchema = z.object({
  datasetIds: z.array(z.string().min(1)).min(1),
})
export type DatasetArchiveRequest = z.infer<typeof datasetArchiveRequestSchema>

/** `pending`: waiting or being built. `expired`: the archive is no longer kept. */
export const DATASET_ARCHIVE_STATUSES = ['pending', 'completed', 'error', 'expired'] as const
export const datasetArchiveStatusSchema = z.enum(DATASET_ARCHIVE_STATUSES)
export type DatasetArchiveStatus = z.infer<typeof datasetArchiveStatusSchema>

export const datasetArchiveJobSchema = z.object({
  id: z.string().min(1),
  status: datasetArchiveStatusSchema,
  datasetCount: countSchema,
  /** Sum of the sizes of the datasets' files. */
  totalBytes: countSchema,
  fileName: z.string().min(1),
  archiveBytes: countSchema.nullable(),
  /** Only for `completed`. A new address comes with every answer. */
  downloadUrl: z.string().min(1).nullable(),
  /** Only for `completed`: `downloadUrl` stops working at this time. */
  expiresAt: utcTimestampSchema.nullable(),
  /** Only for `completed`: the archive is kept until this time; then the job is `expired`. */
  availableUntil: utcTimestampSchema.nullable(),
  /** Only for `error`: the reason, an English sentence for the user. */
  errorMessage: z.string().nullable(),
  createdAt: utcTimestampSchema,
  finishedAt: utcTimestampSchema.nullable(),
})
export type DatasetArchiveJob = z.infer<typeof datasetArchiveJobSchema>

/** Body of a refusal that is about datasets: which ones. */
export const datasetIdsErrorSchema = z.object({
  code: z.string(),
  datasetIds: z.array(z.string()).min(1),
})
